import React, { useCallback } from 'react';
import {
	InputAdornment,
	makeStyles,
	TextField,
	Theme,
	Button,
} from '@material-ui/core';
import { CloudUpload, Close } from '@material-ui/icons';
import { Control, FieldValues, DeepMap, FieldError, Controller } from 'react-hook-form';
import InputTooltip from 'components/tooltips/InputTooltips';

const useStyles = makeStyles((theme: Theme) => ({
	input: {
		width: '100%',
		'& .MuiOutlinedInput-root': {
			backgroundColor: 'white',
		},
	},
	hiddenInput: {
		display: 'none',
	},
	uploadButton: {
		marginRight: theme.spacing(1),
	},
	fileName: {
		marginLeft: theme.spacing(1),
		flex: 1,
	},
	clearButton: {
		padding: 4,
	},
}));

interface InputFileUploadProps {
	label: string;
	name: string;
	accept?: string;
	tooltip?: string;
	control: Control<FieldValues>;
	errors: DeepMap<FieldValues, FieldError>;
	disabled?: boolean;
	maxSize?: number; // in bytes
}


export const InputFileUpload = ({
																	label,
																	name,
																	accept,
																	tooltip,
																	control,
																	errors,
																	disabled,
																	maxSize,
																}: InputFileUploadProps) => {
	const classes = useStyles();

	const handleFileChange = useCallback((onChange: (value: File | null) => void) =>
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0] || null;

			if (file && maxSize && file.size > maxSize) {
				// You might want to handle this error through form validation
				alert(`File size must be less than ${maxSize / 1024 / 1024}MB`);
				return;
			}
			onChange(file);
		}, [maxSize]);

	const clearFile = useCallback((onChange: (value: null) => void) => () => {
		onChange(null);
	}, []);

	return (
		<Controller
			name={name}
			control={control}
			defaultValue={null}
			render={({ field: { onChange, value } }) => (
				<TextField
					disabled={disabled}
					color="primary"
					variant="outlined"
					label={label}
					className={classes.input}
					style={{ marginTop: 24 }}
					error={errors[name] !== undefined}
					helperText={errors[name]?.message}
					InputLabelProps={{
						shrink: true,
					}}
					InputProps={{
						readOnly: true,
						startAdornment: (
							<InputAdornment position="start">
								<input
									accept={accept}
									className={classes.hiddenInput}
									id={`file-upload-${name}`}
									type="file"
									onChange={(e) => handleFileChange(onChange)(e)}
									disabled={disabled}
								/>
								<label htmlFor={`file-upload-${name}`}>
									<Button
										component="span"
										variant="contained"
										color="primary"
										className={classes.uploadButton}
										startIcon={<CloudUpload />}
										disabled={disabled}
									>
										Upload
									</Button>
								</label>
							</InputAdornment>
						),
						endAdornment: (
							<>
								{value && (
									<InputAdornment position="end">
										<Button
											className={classes.clearButton}
											onClick={clearFile(onChange)}
											disabled={disabled}
										>
											<Close />
										</Button>
									</InputAdornment>
								)}
								{tooltip && <InputTooltip title={tooltip} error={false} />}
							</>
						),
					}}
					value={value ? value.name : ''}
				/>
			)}
		/>
	);
};